import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/currency';

const GeneralLedgerTable = ({ transactions, sortConfig, onSort, onViewTransaction }) => {
  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={16} className="text-primary" />
      : <Icon name="ChevronDown" size={16} className="text-primary" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTypeColor = (type) => {
    const typeColors = {
      'Invoice': 'text-primary',
      'Invoice Payment': 'text-success',
      'Expense': 'text-error',
      'Journal Entry': 'text-secondary',
      'Payroll': 'text-warning',
      'Bank Transaction': 'text-accent'
    };
    return typeColors?.[type] || 'text-foreground';
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('date')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Date</span>
                  {getSortIcon('date')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('account')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Account</span>
                  {getSortIcon('account')}
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">Description</div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">Reference</div>
              </th>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('type')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Type</span>
                  {getSortIcon('type')}
                </div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Debit</div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Credit</div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Balance</div>
              </th>
              <th className="px-4 py-3 text-center w-20">
                <div className="font-heading font-semibold text-sm text-foreground">Action</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions?.map((transaction) => (
              <tr key={transaction?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{formatDate(transaction?.date)}</span>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <span className="font-medium text-foreground block">{transaction?.account}</span>
                    <span className="text-xs text-muted-foreground">{transaction?.source}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{transaction?.description}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground font-mono">{transaction?.reference}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-sm font-medium ${getTypeColor(transaction?.type)}`}>
                    {transaction?.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-medium text-foreground">
                    {transaction?.debit > 0 ? formatCurrency(transaction?.debit) : '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-medium text-foreground">
                    {transaction?.credit > 0 ? formatCurrency(transaction?.credit) : '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-semibold text-foreground">{formatCurrency(transaction?.balance)}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewTransaction(transaction)}
                    title="View Details"
                  >
                    <Icon name="Eye" size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GeneralLedgerTable;